import { useState } from 'react';
import type { ReactNode } from 'react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { NeobrutalButton } from '@/components/marketing/v2/NeobrutalButton';

const FAQ: ReadonlyArray<{ q: string; a: ReactNode }> = [
  {
    q: 'Sur Mac, l\'app ne s\'ouvre toujours pas après « clic droit → Ouvrir »',
    a: (
      <>
        Ouvre le Terminal et colle&nbsp;:{' '}
        <code className="font-marketing-mono text-[13px] text-charcoal-800">
          xattr -d com.apple.quarantine "/Applications/Velko POS.app"
        </code>
        . Vérifie que l'app est bien dans le dossier Applications et que les guillemets sont présents
        (le chemin contient une espace).
      </>
    ),
  },
  {
    q: 'Le Terminal répond « xattr: No such file… »',
    a: 'Le chemin est incorrect. L\'app doit s\'appeler exactement « Velko POS.app » et se trouver dans /Applications. Glisse d\'abord l\'app depuis le .dmg vers Applications, puis relance la commande.',
  },
  {
    q: 'Windows continue de bloquer le .exe',
    a: 'Au lieu de double-cliquer, fais un clic droit sur le fichier → Propriétés → coche « Débloquer » en bas → Appliquer. Relance ensuite l\'installeur. Le .msi est aussi une alternative pour les postes en entreprise.',
  },
  {
    q: 'Sur Linux, l\'AppImage ne se lance pas',
    a: 'Assure-toi de l\'avoir rendue exécutable (chmod +x). Sur certaines distributions, installe libfuse2 : « sudo apt install libfuse2 ». Sinon, privilégie le paquet .deb ou .rpm.',
  },
  {
    q: 'Comment vérifier que le fichier est authentique ?',
    a: (
      <>
        Télécharge toujours depuis la page{' '}
        <a
          href="https://github.com/douglace/velko-pos-releases/releases"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-charcoal-800 underline decoration-lime-500 decoration-2 underline-offset-2 hover:text-lime-600"
        >
          GitHub Releases officielle
        </a>{' '}
        ou via le bouton de notre page de téléchargement. Les binaires y sont publiés publiquement
        pour chaque version.
      </>
    ),
  },
];

export default function InstallFaqV2() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="bg-white py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-6 md:px-8">
        <div className="text-center">
          <SectionLabel className="justify-center">Dépannage</SectionLabel>
          <h2 className="mt-4 font-marketing-display text-[clamp(30px,4.2vw,54px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
            Ça coince ? <span className="underline-lime">On a la réponse.</span>
          </h2>
        </div>

        <ul className="mt-12 space-y-4">
          {FAQ.map((item, idx) => {
            const isOpen = open === idx;
            return (
              <li key={item.q} className="border-2 border-charcoal-800 bg-paper shadow-neobrutal-4">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : idx)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-marketing-display text-[16px] font-bold text-charcoal-800 md:text-lg cursor-pointer"
                >
                  <span>{item.q}</span>
                  <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center border-2 border-charcoal-800 transition-colors ${isOpen ? 'bg-lime-500 text-charcoal-800' : 'bg-white text-charcoal-700'}`}
                    aria-hidden="true"
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      className={`transition-transform ${isOpen ? 'rotate-45' : ''}`}
                    >
                      <path d="M7 2v10" />
                      <path d="M2 7h10" />
                    </svg>
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t-2 border-charcoal-800 px-6 py-5 font-marketing-body text-[15px] leading-relaxed text-charcoal-600">
                    {item.a}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-12 text-center">
          <p className="mb-5 font-marketing-body text-[15px] text-charcoal-600">
            Toujours bloqué ? On répond en moins de 24 h.
          </p>
          <NeobrutalButton as="link" to="/contact" variant="primary" size="md">
            Contacter le support
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14" />
              <path d="M13 6l6 6-6 6" />
            </svg>
          </NeobrutalButton>
        </div>
      </div>
    </section>
  );
}
